import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Award, Download, Calendar, Users, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DiarioAttivita() {
  const [diario, setDiario] = useState({ voci: [], totali: {} });
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    fetchDiario();
  }, [anno]);

  const fetchDiario = async () => {
    try {
      let url = `${API_URL}/api/diario`;
      if (anno !== 'tutti') {
        url += `?anno=${anno}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setDiario(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      let url = `${API_URL}/api/diario/export`;
      if (anno !== 'tutti') {
        url += `?anno=${anno}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `diario_attivita_${anno}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('PDF scaricato con successo');
      }
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="diario-attivita-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Diario Attività</h1>
          <p className="page-subtitle">Registro cronologico delle attività dell'associazione</p>
        </div>
        <div className="flex gap-2">
          <Select value={anno} onValueChange={setAnno}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleExportPDF}>
            <Download className="w-4 h-4 mr-2" />
            Esporta PDF
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#0055BB]/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#0055BB]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{diario.totali?.eventi || 0}</p>
              <p className="text-sm text-slate-500">Eventi Registrati</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#008A55]/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-[#008A55]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{diario.totali?.ore_donate || 0}</p>
              <p className="text-sm text-slate-500">Ore Donate</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{diario.totali?.partecipazioni_totali || 0}</p>
              <p className="text-sm text-slate-500">Partecipazioni Totali</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-[#0055BB]" />
            Registro Attività {anno !== 'tutti' ? anno : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {diario.voci?.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="empty-state-icon" />
              <p className="empty-state-title">Nessuna voce nel diario</p>
              <p className="empty-state-text">Le voci vengono generate automaticamente al completamento degli eventi</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Evento</TableHead>
                  <TableHead className="text-center">Volontari</TableHead>
                  <TableHead className="text-center">Ore Donate</TableHead>
                  <TableHead className="hidden md:table-cell">Descrizione</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {diario.voci.map((voce, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Badge variant="outline">{voce.data_evento}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{voce.titolo_evento}</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1">
                        <Users className="w-4 h-4 text-slate-400" />
                        {voce.volontari_partecipanti}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 font-semibold text-[#008A55]">
                        <Clock className="w-4 h-4" />
                        {voce.ore_totali_donate?.toFixed(1)} h
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-500 max-w-xs truncate">
                      {voce.descrizione || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <div className="alert-info">
        <p className="font-medium mb-1">Utilizzo del Diario Attività</p>
        <ul className="text-sm space-y-1">
          <li>• Il diario viene popolato automaticamente quando un evento viene concluso e si genera la voce</li>
          <li>• Esporta il PDF per includere i dati nelle relazioni annuali e nel bilancio sociale</li>
          <li>• Ogni voce include il numero di volontari partecipanti e le ore totali donate</li>
        </ul>
      </div>
    </div>
  );
}
